import ForgotPasswordPage from "authentication/components/login/page/ForgotPasswordPage.vue";
import LoginPage from "authentication/components/login/page/LoginPage.vue";
import NotFoundPage from "authentication/components/login/page/NotFoundPage.vue";
import ResetPasswordPage from "authentication/components/login/page/ResetPasswordPage.vue";
import VerifyPage from "authentication/components/login/page/VerifyPage.vue";
import AccessibilityPreferences from "authentication/pages/mydetails/AccessibilityPreferences.vue";
import ChangePasswordPage from "authentication/pages/mydetails/ChangePasswordPage.vue";
import ContactDetailsPage from "authentication/pages/mydetails/ContactDetailsPage.vue";
import DocumentPreferencesPage from "authentication/pages/mydetails/DocumentPreferencesPage.vue";
import EsignaturesPage from "authentication/pages/mydetails/EsignaturesPage.vue";
import { useUserAccountsStore } from "authentication/stores/user-accounts.store.ts";
import { useConfigOptionStore } from "control-panel/control-panel.globals";
import { actionStart } from "core/js/load-tracker.js";
import springUrl from "core/js/spring-url.js";
import { useCurrentSessionStore } from "core/store/current-session.store";
import { createRouter, createWebHistory } from "vue-router";

export const routes = [
  {
    path: springUrl("authentication/login"),
    name: "login-page",
    component: LoginPage,
    meta: { documentTitle: "Sign in" },
    props: route => ({
      credentialLogin: route.query.credentialLogin === "true",
      passwordChanged: route.query.passwordChanged === "true",
    }),
  },
  {
    path: springUrl("authentication/login/verify"),
    name: "verify-page",
    component: VerifyPage,
    meta: { documentTitle: "Verify your identity" },
  },
  {
    path: springUrl("authentication/login/forgot-password"),
    name: "forgot-password-page",
    component: ForgotPasswordPage,
    meta: { documentTitle: "Request password reset" },
  },
  {
    path: springUrl("authentication/login/reset-password"),
    name: "reset-password-page",
    component: ResetPasswordPage,
    meta: { documentTitle: "Reset your password" },
  },
  {
    path: springUrl("authentication/my-details"),
    redirect: { name: "contact-details" },
  },
  {
    path: springUrl("authentication/my-details/contact-details"),
    name: "contact-details",
    component: ContactDetailsPage,
    meta: { documentTitle: "My Details" },
  },
  {
    path: springUrl("authentication/my-details/accessibility-preferences"),
    name: "accessibility-preferences",
    component: AccessibilityPreferences,
    meta: { documentTitle: "My Details" },
  },
  {
    path: springUrl("authentication/my-details/esignatures"),
    name: "esignatures",
    component: EsignaturesPage,
    meta: { documentTitle: "My Details" },
    beforeEnter: async (to, from, next) => {
      const configOptionStore = useConfigOptionStore();
      await configOptionStore.isReady();

      if (!configOptionStore.isConfigOptionEnabled("ESIGNATURES")) {
        next({ name: "contact-details" });
      } else {
        next();
      }
    },
  },
  {
    path: springUrl("authentication/my-details/change-password"),
    name: "change-password",
    component: ChangePasswordPage,
    meta: { documentTitle: "My Details" },
    beforeEnter: async (to, from, next) => {
      const userAccountStore = useUserAccountsStore();
      const currentSessionStore = useCurrentSessionStore();
      await Promise.all([userAccountStore.isReady(), currentSessionStore.isReady()]);

      // If a user doesn't currently have a password set, they should not be allowed to access the change password page
      const userId = currentSessionStore.getUserId;
      if (userId) {
        const user = userAccountStore.getUser(userId);
        if (user?.passwordSet) {
          next();
        } else {
          next({ name: "contact-details" });
        }
      } else {
        next({ name: "contact-details" });
      }
    },
  },
  {
    path: springUrl("authentication/my-details/document-preferences"),
    name: "user-document-preferences",
    component: DocumentPreferencesPage,
    meta: { documentTitle: "My Details" },
  },
  {
    path: "/:pathMatch(.*)*",
    component: NotFoundPage,
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach((to, from, next) => {
  if (to.meta?.documentTitle) {
    document.title = `${to.meta.documentTitle} - eCase`;
  }

  actionStart(to.name);
  next();
});

export default router;
